import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  setPersistence,
  browserLocalPersistence,
  type Auth,
  type User,
} from 'firebase/auth'
import { orderBy, Timestamp, type QueryConstraint, type UpdateData } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getFirebaseApp } from '../config/firebase.config'
import { refs } from '../models/base/firestore-refs'
import { getAll, getOne, updateWithUpdatedAt } from './firestore-crud'
import type { AdminAccount } from '../models/account/admin/admin-account.modl'
import {
  toAdminAccount,
  type AdminAccountDoc,
} from '../models/account/admin/admin-account.modl'
import type { SchoolAccount } from '../models/account/school/school-account.modl'
import {
  toSchoolAccount,
  type SchoolAccountDoc,
} from '../models/account/school/school-account.modl'

import {
  validatePassword,
  PASSWORD_REQUIREMENT_MESSAGE,
} from '../utils/password-validator'

export { PASSWORD_REQUIREMENT_MESSAGE }

export type FirstLoginPasswordValidationErrors = {
  initialPassword?: string
  newPassword?: string
  confirmPassword?: string
}

export type ChangePasswordValidationErrors = {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

export type ValidateFirstLoginPasswordChangeInput = {
  initialPassword: string
  newPassword: string
  confirmPassword: string
}

export type FirstLoginPasswordChangeAccountCollection = 'admin_accounts' | 'school_accounts'

export type SchoolLoginErrorCode = 'USER_ID_FAILED' | 'ACCOUNT_NOT_FOUND' | 'ACCOUNT_DEACTIVATED'

export class SchoolLoginError extends Error {
  readonly code: SchoolLoginErrorCode

  constructor(code: SchoolLoginErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'SchoolLoginError'
    this.code = code
  }
}

export type AdminLoginErrorCode = 'USER_ID_FAILED' | 'ACCOUNT_NOT_FOUND' | 'ACCOUNT_DEACTIVATED'

export class AdminLoginError extends Error {
  readonly code: AdminLoginErrorCode

  constructor(code: AdminLoginErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'AdminLoginError'
    this.code = code
  }
}

export type FirstLoginPasswordChangeErrorCode =
  | 'missing-user'
  | 'missing-email'
  | 'wrong-password'
  | 'weak-password'
  | 'requires-recent-login'
  | 'unknown'

export class FirstLoginPasswordChangeError extends Error {
  readonly code: FirstLoginPasswordChangeErrorCode

  constructor(code: FirstLoginPasswordChangeErrorCode, message?: string) {
    super(message ?? code)
    this.code = code
    this.name = 'FirstLoginPasswordChangeError'
  }
}

export class AccountService {
  /**
   * 管理者アカウントを1件取得する
   * @param uid - 管理者のUID（admin_accounts のドキュメントID）
   * @param includeDeleted - 論理削除済みを含めるか（デフォルト: false）
   * @returns 取得した AdminAccount、存在しない場合は null
   */
  async getAdminAccount(uid: string, includeDeleted: boolean = false): Promise<AdminAccount | null> {
    return getOne(refs.adminAccount(uid), toAdminAccount, { includeDeleted })
  }

  /**
   * 管理者アカウント一覧を取得する（未削除のみ、登録日降順）
   */
  async getAdminAccounts(includeDeleted: boolean = false): Promise<AdminAccount[]> {
    const constraints: QueryConstraint[] = [orderBy('created_at', 'desc')]
    return getAll(
      refs.adminAccounts(),
      (id, data, path) => toAdminAccount(id, data as AdminAccountDoc, path),
      { includeDeleted, constraints },
    )
  }

  /**
   * 管理者アカウントの単一フィールドを更新する（name_kanji / name_kana）
   * @param accountId - 管理者アカウントID（UID）
   * @param field - フィールド名（nameKanji または nameKana）
   * @param value - 更新する値
   * @returns 更新後の AdminAccount、失敗時は null
   */
  async updateAdminAccountField(
    accountId: string,
    field: 'nameKanji' | 'nameKana',
    value: string,
  ): Promise<AdminAccount | null> {
    const fieldMap: Record<string, string> = {
      nameKanji: 'name_kanji',
      nameKana: 'name_kana',
    }
    const firestoreField = fieldMap[field]
    if (!firestoreField) return null
    const docRef = refs.adminAccount(accountId)
    await updateWithUpdatedAt(docRef, { [firestoreField]: value.trim() } as UpdateData<AdminAccountDoc>)
    return this.getAdminAccount(accountId)
  }

  /**
   * 初回ログインのパスワード変更バリデーション（UIメッセージ込み）
   */
  validateFirstLoginPasswordChange(
    input: ValidateFirstLoginPasswordChangeInput,
  ):
    | {
        ok: true
        trimmed: { initialPassword: string; newPassword: string; confirmPassword: string }
      }
    | { ok: false; errors: FirstLoginPasswordValidationErrors } {
    const trimmedInitialPassword = input.initialPassword.trim()
    const trimmedNewPassword = input.newPassword.trim()
    const trimmedConfirmPassword = input.confirmPassword.trim()

    const errors: FirstLoginPasswordValidationErrors = {}

    if (!trimmedInitialPassword) {
      errors.initialPassword = '初回ログインパスワードは必須です'
    }

    if (!trimmedNewPassword) {
      errors.newPassword = '新しいパスワードは必須です'
    } else {
      if (trimmedInitialPassword && trimmedInitialPassword === trimmedNewPassword) {
        errors.newPassword = '新しいパスワードは現在のパスワードと異なる必要があります'
      } else if (!validatePassword(trimmedNewPassword)) {
        errors.newPassword = PASSWORD_REQUIREMENT_MESSAGE
      }
    }

    if (!trimmedConfirmPassword) {
      errors.confirmPassword = 'パスワードの再入力は必須です'
    } else if (trimmedNewPassword && trimmedNewPassword !== trimmedConfirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません'
    }

    if (errors.initialPassword || errors.newPassword || errors.confirmPassword) {
      return { ok: false, errors }
    }

    return {
      ok: true,
      trimmed: {
        initialPassword: trimmedInitialPassword,
        newPassword: trimmedNewPassword,
        confirmPassword: trimmedConfirmPassword,
      },
    }
  }

  /**
   * マイページ等のパスワード変更バリデーション（現在のパスワード・新パスワード・確認）
   * password-validator のルール（6文字以上・半角小文字英字と数字を各1文字以上・小文字英字と数字のみ）を使用
   */
  validateChangePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ):
    | { ok: true; trimmed: { currentPassword: string; newPassword: string; confirmPassword: string } }
    | { ok: false; errors: ChangePasswordValidationErrors } {
    const trimmedCurrent = currentPassword.trim()
    const trimmedNew = newPassword.trim()
    const trimmedConfirm = confirmPassword.trim()
    const errors: ChangePasswordValidationErrors = {}

    if (!trimmedCurrent) errors.currentPassword = '現在のパスワードは必須です'

    if (!trimmedNew) {
      errors.newPassword = '新しいパスワードは必須です'
    } else {
      if (trimmedCurrent && trimmedCurrent === trimmedNew) {
        errors.newPassword = '新しいパスワードは現在のパスワードと異なる必要があります'
      } else if (!validatePassword(trimmedNew)) {
        errors.newPassword = PASSWORD_REQUIREMENT_MESSAGE
      }
    }

    if (!trimmedConfirm) errors.confirmPassword = 'パスワードの再入力は必須です'
    else if (trimmedNew && trimmedNew !== trimmedConfirm) errors.confirmPassword = 'パスワードが一致しません'

    if (errors.currentPassword || errors.newPassword || errors.confirmPassword) {
      return { ok: false, errors }
    }
    return {
      ok: true,
      trimmed: { currentPassword: trimmedCurrent, newPassword: trimmedNew, confirmPassword: trimmedConfirm },
    }
  }

  /**
   * パスワード変更（再認証 → updatePassword のみ。signOut は呼び出し元で行う）
   * @throws FirstLoginPasswordChangeError - missing-user / missing-email / wrong-password / weak-password / requires-recent-login / unknown
   */
  async changePassword(params: {
    user: User | null
    currentPassword: string
    newPassword: string
  }): Promise<void> {
    const { user, currentPassword, newPassword } = params
    if (!user) throw new FirstLoginPasswordChangeError('missing-user')
    if (!user.email) throw new FirstLoginPasswordChangeError('missing-email')

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      await updatePassword(user, newPassword)
    } catch (err: any) {
      const code = String(err?.code ?? '')
      if (code === 'auth/wrong-password') throw new FirstLoginPasswordChangeError('wrong-password')
      if (code === 'auth/weak-password') throw new FirstLoginPasswordChangeError('weak-password')
      if (code === 'auth/requires-recent-login') throw new FirstLoginPasswordChangeError('requires-recent-login')
      throw new FirstLoginPasswordChangeError('unknown')
    }
  }

  /**
   * 初回ログイン: (再認証) → updatePassword → Firestore に last_login_at/updated_at を付与 → signOut
   *
   * UI側で toast / navigate を行うため、ここでは例外（code付き）だけ投げる。
   */
  async changePasswordOnFirstLogin(params: {
    auth: Auth
    user: User | null
    accountCollection: FirstLoginPasswordChangeAccountCollection
    initialPassword: string
    newPassword: string
  }): Promise<void> {
    const { auth, user, accountCollection, initialPassword, newPassword } = params

    if (!user) throw new FirstLoginPasswordChangeError('missing-user')
    if (!user.email) throw new FirstLoginPasswordChangeError('missing-email')

    try {
      const credential = EmailAuthProvider.credential(user.email, initialPassword)
      await reauthenticateWithCredential(user, credential)
      await updatePassword(user, newPassword)

      const accountDocRef =
        accountCollection === 'admin_accounts'
          ? refs.adminAccount(user.uid)
          : refs.schoolAccount(user.uid)
      await updateWithUpdatedAt(accountDocRef, {
        last_login_at: Timestamp.now(),
      } as UpdateData<AdminAccountDoc & SchoolAccountDoc>)

      await signOut(auth)
    } catch (err: any) {
      const code = String(err?.code ?? '')
      if (code === 'auth/wrong-password') throw new FirstLoginPasswordChangeError('wrong-password')
      if (code === 'auth/weak-password') throw new FirstLoginPasswordChangeError('weak-password')
      if (code === 'auth/requires-recent-login') throw new FirstLoginPasswordChangeError('requires-recent-login')
      throw new FirstLoginPasswordChangeError('unknown')
    }
  }


  /**
   * パスワードリセットメール送信（Callable requestPasswordReset を呼ぶ）
   * @param email - 送信先メールアドレス（trim 済みを想定）
   * @param continueUrl - リセット後の遷移URL（省略可）
   * @throws HttpsError - invalid-argument 等（呼び出し元で err.code / err.message を参照）
   */
  async requestPasswordReset(email: string, continueUrl?: string): Promise<void> {
    const functions = getFunctions(getFirebaseApp(), 'asia-northeast1')
    const fn = httpsCallable<
      { email: string; continueUrl?: string },
      { success: boolean }
    >(functions, 'requestPasswordReset')
    await fn({
      email: email.trim(),
      ...(continueUrl ? { continueUrl } : {}),
    })
  }

  /**
   * 管理者アカウントでログインする
   * - Firebase Auth で認証
   * - admin_accounts を取得して検証（存在・無効化チェック）
   * - 初回ログイン判定（lastLoginAt が null）
   *
   * @throws AdminLoginError - アカウント検証失敗時（signOut 済み）
   * @throws Firebase Auth エラー - 認証失敗時（invalid-credential 等）
   */
  async loginAsAdmin(
    email: string,
    password: string,
  ): Promise<{ account: AdminAccount; isFirstLogin: boolean }> {
    const auth = getAuth(getFirebaseApp())
    await setPersistence(auth, browserLocalPersistence)
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    if (!user.uid) {
      await signOut(auth)
      throw new AdminLoginError('USER_ID_FAILED', 'ユーザーIDの取得に失敗しました')
    }

    // ログアウト後の再ログインで Firestore が古いトークンを使うと permission-denied になるため、
    // カスタムクレーム（role: admin）を含むトークンを明示的に取得してから Firestore を読む
    await user.getIdToken(true)

    const account = await this.getAdminAccount(user.uid, false)
    if (!account) {
      await signOut(auth)
      throw new AdminLoginError('ACCOUNT_NOT_FOUND', '管理者アカウントが見つかりません')
    }
    if (account.is_deleted) {
      await signOut(auth)
      throw new AdminLoginError('ACCOUNT_DEACTIVATED', 'このアカウントは無効化されています')
    }

    const isFirstLogin = !account.lastLoginAt
    if (!isFirstLogin) {
      const now = Timestamp.now()
      try {
        await updateWithUpdatedAt(
          refs.adminAccount(user.uid),
          { last_login_at: now } as UpdateData<AdminAccountDoc>,
        )
        account.lastLoginAt = now.toDate()
      } catch (error) {
        console.warn('管理者の最終ログイン日時更新に失敗しました:', error)
      }
    }
    return { account, isFirstLogin }
  }

  /**
   * 学校アカウントでログインする
   * - Firebase Auth で認証
   * - school_accounts を取得して検証（存在・無効化チェック）
   * - 初回ログイン判定（lastLoginAt が null）
   *
   * @throws SchoolLoginError - アカウント検証失敗時（signOut 済み）
   * @throws Firebase Auth エラー - 認証失敗時（invalid-credential 等）
   */
  async loginAsSchool(
    email: string,
    password: string,
  ): Promise<{ account: SchoolAccount; isFirstLogin: boolean }> {
    const auth = getAuth(getFirebaseApp())
    await setPersistence(auth, browserLocalPersistence)
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    if (!user.uid) {
      await signOut(auth)
      throw new SchoolLoginError('USER_ID_FAILED', 'ユーザーIDの取得に失敗しました')
    }

    const account = await getOne(
      refs.schoolAccount(user.uid),
      (id, data, path) => toSchoolAccount(id, data as SchoolAccountDoc, path),
      { includeDeleted: false },
    )
    if (!account) {
      await signOut(auth)
      throw new SchoolLoginError('ACCOUNT_NOT_FOUND', '学校アカウントが見つかりません')
    }
    if (account.is_deleted) {
      await signOut(auth)
      throw new SchoolLoginError('ACCOUNT_DEACTIVATED', 'このアカウントは無効化されています')
    }

    const isFirstLogin = !account.lastLoginAt
    return { account, isFirstLogin }
  }
}

